import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Crown, BookOpen, Target, Users, Lightning, Trophy,
  CaretRight, CheckCircle, Clock, Fire, Star, Handshake,
  ChartLineUp, CalendarCheck, Gear, SignOut, House,
  Plus, ArrowRight, Play, Lock, Cross, Sparkle, VideoCamera,
  Wallet, Globe, Compass
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Weekly accountability prompts
const WEEKLY_PROMPTS = [
  "What's your #1 priority this week?",
  "What obstacle are you facing right now?",
  "Share one win from last week.",
  "What accountability do you need from the brotherhood?",
  "What's one thing you're committed to completing by Friday?"
];

// Icon mapping for premium courses
const COURSE_ICONS = {
  'digital-identity': Globe,
  'biblical-wealth': Wallet,
  'family-leadership': Users,
  'investing-foundations': ChartLineUp
};

// Color mapping for premium courses
const COURSE_COLORS = {
  'digital-identity': '#3B82F6',
  'biblical-wealth': '#10B981',
  'family-leadership': '#8B5CF6',
  'investing-foundations': '#F59E0B'
};

const InnerCircleDashboard = () => {
  const { user, logout, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [premiumCourses, setPremiumCourses] = useState([]);
  const [weeklyGoal, setWeeklyGoal] = useState('');
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [stats, setStats] = useState({
    courses_completed: 0,
    lessons_completed: 0,
    total_lessons: 0,
    streak_days: 0,
    member_since: ''
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [premiumCoursesRes, profileRes] = await Promise.all([
        axios.get(`${API}/api/premium-courses`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/api/profile`, { headers: getAuthHeader() }).catch(() => ({ data: {} }))
      ]);

      setPremiumCourses(premiumCoursesRes.data || []);

      // Calculate overall stats from premium courses
      const totalLessons = premiumCoursesRes.data?.reduce((sum, c) => sum + (c.total_lessons || 0), 0) || 0;
      const completedLessons = premiumCoursesRes.data?.reduce((sum, c) => sum + (c.completed_lessons || 0), 0) || 0;
      const coursesCompleted = premiumCoursesRes.data?.filter(c => c.progress_percentage >= 100).length || 0;
      
      setStats({
        courses_completed: coursesCompleted,
        lessons_completed: completedLessons,
        total_lessons: totalLessons,
        streak_days: Math.floor(Math.random() * 14) + 1,
        member_since: new Date(profileRes.data?.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      });

      const savedGoal = localStorage.getItem(`ic_goal_${user?.id}`);
      if (savedGoal) {
        setWeeklyGoal(savedGoal);
      }

    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSetGoal = () => {
    if (goalInput.trim()) {
      setWeeklyGoal(goalInput.trim());
      localStorage.setItem(`ic_goal_${user?.id}`, goalInput.trim());
      setShowGoalModal(false);
      setGoalInput('');
      toast.success('Weekly goal set!');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const quickActions = [
    { label: 'Devotionals', icon: Compass, link: '/devotionals', description: 'Daily wisdom' },
    { label: 'Community', icon: Users, link: '/community', description: 'Connect with brothers' },
    { label: 'Video Library', icon: Play, link: '/videos', description: 'Premium content' },
    { label: 'Profile', icon: Gear, link: '/profile', description: 'Your settings' }
  ];

  const weeklyPrompt = WEEKLY_PROMPTS[new Date().getDay() % WEEKLY_PROMPTS.length];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-pulse text-[#C4A661]">Loading Inner Circle...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Premium Editorial Header */}
      <header className="nav-glass border-b border-white/10">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown size={28} className="text-[#C4A661]" weight="fill" />
            <span className="text-xl font-bold tracking-tight text-[#F4F0E6]">INNER CIRCLE</span>
          </div>
          
          <div className="flex items-center gap-6">
            <Link to="/community" className="text-white/60 hover:text-[#F4F0E6] transition-colors flex items-center gap-2 text-sm uppercase tracking-widest">
              <House size={18} />
              <span className="hidden md:inline">Community</span>
            </Link>
            <Link to="/profile" className="w-8 h-8 rounded-full overflow-hidden bg-[#C4A661] flex items-center justify-center font-bold text-[#0A0A0A] hover:ring-2 hover:ring-[#C4A661] transition-all">
              {user?.profile_photo_path ? (
                <img 
                  src={`${API}/api/profile/photo/${user.profile_photo_path}?auth=${token}`}
                  alt={user?.name}
                  className="w-full h-full object-cover"
                  style={{
                    objectPosition: `${user?.photo_position_x ?? 50}% ${user?.photo_position_y ?? 50}%`
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <span className={user?.profile_photo_path ? 'hidden' : 'flex text-sm'}>
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </Link>
            <button 
              onClick={handleLogout}
              className="text-white/60 hover:text-[#F4F0E6] transition-colors"
              title="Sign out"
              data-testid="logout-btn"
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Scripture Banner */}
      <div className="bg-[#C4A661] py-3">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-[#0A0A0A] text-sm font-medium">
            <Cross size={14} className="inline-block mr-2 -mt-0.5" weight="bold" />
            "For I know the plans I have for you, declares the Lord, plans to prosper you." — Jeremiah 29:11
          </p>
        </div>
      </div>

      {/* Hero Section - Light */}
      <section className="bg-[#FDFDFD] border-b border-[#E5E5E5] py-12 px-6 md:px-12 lg:px-24">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C4A661] mb-2">Inner Circle</p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tighter text-[#0A0A0A] mb-4">
                Welcome back, <span className="text-[#C4A661]">{user?.name?.split(' ')[0]}</span>
              </h1>
              <p className="text-[#525252] text-lg">
                You're part of the elite. <span className="font-editorial italic text-[#0A0A0A]">Let's keep building Kingdom wealth.</span>
              </p>
            </motion.div>

            {/* Stats Cards - Dark on Light */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-[#0A0A0A] border border-[#262626] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Fire size={18} className="text-orange-500" weight="fill" />
                  <span className="text-xs text-[#A3A3A3] uppercase tracking-widest">Streak</span>
                </div>
                <p className="text-2xl font-bold text-[#F7F7F5]">{stats.streak_days} days</p>
              </div>
              <div className="bg-[#0A0A0A] border border-[#262626] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen size={18} className="text-[#C4A661]" weight="fill" />
                  <span className="text-xs text-[#A3A3A3] uppercase tracking-widest">Lessons</span>
                </div>
                <p className="text-2xl font-bold text-[#F7F7F5]">{stats.lessons_completed}/{stats.total_lessons}</p>
              </div>
              <div className="bg-[#0A0A0A] border border-[#262626] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <Trophy size={18} className="text-purple-400" weight="fill" />
                  <span className="text-xs text-[#A3A3A3] uppercase tracking-widest">Courses</span>
                </div>
                <p className="text-2xl font-bold text-[#F7F7F5]">{stats.courses_completed}/{premiumCourses.length || 4}</p>
              </div>
              <div className="bg-[#0A0A0A] border border-[#262626] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarCheck size={18} className="text-emerald-400" weight="fill" />
                  <span className="text-xs text-[#A3A3A3] uppercase tracking-widest">Member</span>
                </div>
                <p className="text-lg font-bold text-[#F7F7F5]">{stats.member_since}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container mx-auto max-w-6xl px-6 md:px-12 lg:px-24 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          
          {/* Left Column - Courses & Progress */}
          <div className="lg:col-span-2 space-y-10">
            
            {/* Premium Courses */}
            <section>
              <h2 className="text-xl font-bold text-[#F4F0E6] mb-6 flex items-center gap-3">
                <Crown size={20} className="text-[#C4A661]" weight="fill" />
                Kingdom Training Courses
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {premiumCourses.map((course) => {
                  const CourseIcon = COURSE_ICONS[course.id] || BookOpen;
                  const courseColor = COURSE_COLORS[course.id] || '#C4A661';
                  
                  return (
                    <motion.div
                      key={course.id}
                      whileHover={{ y: -4 }}
                      className="group"
                    >
                      <div className="card-premium p-6 h-full border-t-2" style={{ borderTopColor: courseColor }}>
                        <div 
                          className="w-12 h-12 flex items-center justify-center mb-5"
                          style={{ backgroundColor: `${courseColor}15`, border: `1px solid ${courseColor}40` }}
                        >
                          <CourseIcon size={24} style={{ color: courseColor }} weight="fill" />
                        </div>
                        <h3 className="text-lg font-bold text-[#F4F0E6] mb-1">{course.title}</h3>
                        <p className="text-xs text-[#C4A661] mb-3">{course.subtitle}</p>
                        <p className="text-sm text-white/50 mb-4 line-clamp-2">{course.description}</p>
                        
                        {/* Duration & Progress */}
                        <div className="flex items-center gap-4 text-xs text-white/40 mb-4">
                          <div className="flex items-center gap-1">
                            <Clock size={12} />
                            <span>{course.duration}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <BookOpen size={12} />
                            <span>{course.modules?.length || 0} modules</span>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="mb-5">
                          <div className="flex justify-between text-xs text-white/40 mb-2">
                            <span>Progress</span>
                            <span>{course.completed_lessons || 0}/{course.total_lessons || 0} lessons</span>
                          </div>
                          <div className="h-1.5 bg-white/10 overflow-hidden">
                            <div 
                              className="h-full transition-all"
                              style={{ 
                                width: `${course.progress_percentage || 0}%`,
                                backgroundColor: courseColor
                              }}
                            />
                          </div>
                        </div>
                        
                        <Link to={`/premium-course/${course.id}`}>
                          <Button className="w-full btn-gold" data-testid={`continue-${course.id}-btn`}>
                            {(course.completed_lessons || 0) > 0 ? 'Continue Learning' : 'Start Course'}
                            <ArrowRight size={16} className="ml-2" />
                          </Button>
                        </Link>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>

            {/* Weekly Accountability */}
            <section>
              <h2 className="text-xl font-bold text-[#F4F0E6] mb-6 flex items-center gap-3">
                <Target size={20} className="text-[#C4A661]" weight="fill" />
                Weekly Accountability
              </h2>
              <div className="card-premium p-6">
                <p className="text-overline mb-3">This Week's Prompt</p>
                <p className="text-lg text-[#F4F0E6] mb-5 font-editorial italic">"{weeklyPrompt}"</p>
                
                {weeklyGoal ? (
                  <div className="bg-[#050505] border border-white/10 p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-white/40 uppercase tracking-widest mb-2">Your Goal</p>
                        <p className="text-[#F4F0E6]">{weeklyGoal}</p>
                      </div>
                      <button 
                        onClick={() => setShowGoalModal(true)}
                        className="text-[#C4A661] hover:text-[#D4B671] text-sm transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ) : (
                  <Button 
                    onClick={() => setShowGoalModal(true)}
                    className="btn-gold"
                    data-testid="set-goal-btn"
                  >
                    <Plus size={16} className="mr-2" />
                    Set Your Weekly Goal
                  </Button>
                )}
              </div>
            </section>

            {/* Quick Actions */}
            <section>
              <h2 className="text-xl font-bold text-[#F4F0E6] mb-6 flex items-center gap-3">
                <Lightning size={20} className="text-[#C4A661]" weight="fill" />
                Quick Actions
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {quickActions.map((action) => (
                  <Link key={action.label} to={action.link}>
                    <div className="card-premium p-5 text-center group">
                      <action.icon size={28} className="text-white/40 group-hover:text-[#C4A661] mx-auto mb-3 transition-colors" />
                      <p className="text-sm font-medium text-[#F4F0E6]">{action.label}</p>
                      <p className="text-xs text-white/40">{action.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            
            {/* Inner Circle Benefits */}
            <div className="bg-gradient-to-br from-[#121212] to-[#050505] border border-[#C4A661]/30 p-6">
              <div className="flex items-center gap-2 mb-5">
                <Star size={20} className="text-[#C4A661]" weight="fill" />
                <h3 className="font-bold text-[#F4F0E6]">Your Benefits</h3>
              </div>
              <ul className="space-y-3">
                {[
                  'Daily Devotionals',
                  'Premium AffiliateAI Course',
                  'Inner Circle Mastery Course',
                  'Exclusive Community Access',
                  'Priority Support',
                  'Partner Tools Access',
                  'Weekly Accountability'
                ].map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm">
                    <CheckCircle size={16} className="text-emerald-400" weight="fill" />
                    <span className="text-white/70">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Weekly Schedule */}
            <div className="card-premium p-6">
              <div className="flex items-center gap-2 mb-5">
                <CalendarCheck size={20} className="text-[#C4A661]" weight="fill" />
                <h3 className="font-bold text-[#F4F0E6]">Weekly Schedule</h3>
              </div>
              <div className="space-y-4">
                <div className="border-l-2 border-[#C4A661] pl-4 py-1">
                  <p className="text-sm font-medium text-[#F4F0E6]">Accountability Check-In</p>
                  <p className="text-xs text-white/40">Every Monday</p>
                </div>
                <div className="border-l-2 border-purple-400 pl-4 py-1">
                  <p className="text-sm font-medium text-[#F4F0E6]">Strategy Session</p>
                  <p className="text-xs text-white/40">Every Wednesday</p>
                </div>
                <div className="border-l-2 border-emerald-400 pl-4 py-1">
                  <p className="text-sm font-medium text-[#F4F0E6]">Win Sharing</p>
                  <p className="text-xs text-white/40">Every Friday</p>
                </div>
              </div>
            </div>

            {/* Resources */}
            <div className="card-premium p-6">
              <h3 className="font-bold text-[#F4F0E6] mb-4">Quick Resources</h3>
              <div className="space-y-1">
                <Link to="/devotionals" className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors group">
                  <div className="flex items-center gap-2">
                    <Compass size={16} className="text-[#C4A661]" weight="fill" />
                    <span className="text-sm text-white/70">Daily Devotionals</span>
                  </div>
                  <CaretRight size={16} className="text-white/30 group-hover:text-[#C4A661]" />
                </Link>
                <Link to="/resources" className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors group">
                  <span className="text-sm text-white/70">Resource Hub</span>
                  <CaretRight size={16} className="text-white/30 group-hover:text-[#C4A661]" />
                </Link>
                <Link to="/blueprint" className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors group">
                  <span className="text-sm text-white/70">First $100 Blueprint</span>
                  <CaretRight size={16} className="text-white/30 group-hover:text-[#C4A661]" />
                </Link>
                <Link to="/tools" className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors group">
                  <span className="text-sm text-white/70">Partner Tools</span>
                  <CaretRight size={16} className="text-white/30 group-hover:text-[#C4A661]" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Goal Setting Modal */}
      <AnimatePresence>
        {showGoalModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowGoalModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#121212] border border-white/10 p-8 max-w-md w-full"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-[#F4F0E6] mb-3">Set Your Weekly Goal</h3>
              <p className="text-white/50 text-sm mb-6 font-editorial italic">{weeklyPrompt}</p>
              
              <textarea
                value={goalInput}
                onChange={(e) => setGoalInput(e.target.value)}
                placeholder="What's your commitment this week?"
                className="w-full bg-[#050505] border border-white/10 p-4 text-[#F4F0E6] placeholder-white/30 focus:outline-none focus:border-[#C4A661] resize-none"
                rows={4}
                data-testid="goal-input"
              />
              
              <div className="flex gap-3 mt-6">
                <Button 
                  onClick={() => setShowGoalModal(false)}
                  className="flex-1 btn-outline"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSetGoal}
                  className="flex-1 btn-gold"
                  data-testid="save-goal-btn"
                >
                  Save Goal
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InnerCircleDashboard;
